/**
 * Merge institutional budget list rows after create/update.
 *
 * List GET can return a stale nested `primary_investigator` (HTTP-cached
 * first page, or a list serializer that lags retrieve). PATCH/retrieve
 * and the edit form picker are the source of truth for the PI column.
 */

/**
 * Resolve PI id from nested user or `primary_investigator_id`.
 *
 * @param {Object|null|undefined} row
 * @returns {string|null}
 */
export function resolveBudgetPiId(row) {
  const pi = row?.primary_investigator;
  const id = pi?.id ?? pi?.user_id ?? row?.primary_investigator_id ?? null;
  return id != null && String(id).trim() !== '' ? String(id) : null;
}

/**
 * Map a user select option `{ value, label }` to nested user shape.
 *
 * @param {{ value?: *, label?: string }|null|undefined} option
 * @returns {Object|null}
 */
export function userSelectOptionToNestedUser(option) {
  if (!option || option.value == null || option.value === '') return null;
  const label = String(option.label || '').trim();
  const parts = label.split(/\s+/).filter(Boolean);
  const firstName = parts[0] || '';
  const lastName = parts.slice(1).join(' ');
  return {
    id: option.value,
    user_id: option.value,
    first_name: firstName,
    last_name: lastName,
    username: label || `User ${option.value}`,
  };
}

/**
 * Build a list-row payload from PATCH/POST data plus the form picker.
 *
 * Prefer the selected PI/related users so the table matches what the
 * user just saved even when the serializer omits or lags nested users.
 *
 * @param {Object|null|undefined} serverRow
 * @param {{
 *   id?: string|number,
 *   selectedPi?: { value?: *, label?: string }|null,
 *   selectedRelated?: Array<{ value?: *, label?: string }>,
 * }} [extras]
 * @returns {Object}
 */
export function hydrateBudgetSaveForList(serverRow, extras = {}) {
  const base =
    serverRow && typeof serverRow === 'object' ? { ...serverRow } : {};
  if (base.id == null && extras.id != null) {
    base.id = extras.id;
  }

  const selectedPi = extras.selectedPi;
  if (selectedPi) {
    base.primary_investigator = userSelectOptionToNestedUser(selectedPi);
    base.primary_investigator_id = selectedPi.value;
  } else {
    base.primary_investigator = null;
    base.primary_investigator_id = null;
  }

  const selectedRelated = Array.isArray(extras.selectedRelated)
    ? extras.selectedRelated
    : null;
  if (selectedRelated) {
    base.related_users = selectedRelated
      .map(userSelectOptionToNestedUser)
      .filter(Boolean);
    base.related_user_ids = selectedRelated
      .map(option => option?.value)
      .filter(value => value != null && value !== '');
  }

  return base;
}

/**
 * Merge a saved budget into an existing list/cache row.
 *
 * @param {Object|null|undefined} existing
 * @param {Object|null|undefined} saved
 * @returns {Object}
 */
export function mergeBudgetListRow(existing, saved) {
  const prev = existing && typeof existing === 'object' ? existing : {};
  if (!saved || typeof saved !== 'object') return { ...prev };

  const merged = { ...prev, ...saved };

  if (saved.primary_investigator) {
    merged.primary_investigator = saved.primary_investigator;
  } else if (
    Object.prototype.hasOwnProperty.call(saved, 'primary_investigator_id') ||
    Object.prototype.hasOwnProperty.call(saved, 'primary_investigator')
  ) {
    const newId = resolveBudgetPiId(saved);
    const oldId = resolveBudgetPiId(prev);
    if (newId == null) {
      merged.primary_investigator = null;
    } else if (newId !== oldId) {
      merged.primary_investigator = null;
    }
  }

  if (
    typeof saved.order_count !== 'number' &&
    typeof prev.order_count === 'number'
  ) {
    merged.order_count = prev.order_count;
  }

  if (
    !Array.isArray(saved.related_users) &&
    Array.isArray(prev.related_users) &&
    Object.prototype.hasOwnProperty.call(saved, 'related_user_ids')
  ) {
    const newIds = (saved.related_user_ids || []).map(id => String(id));
    const oldIds = (prev.related_users || []).map(
      user => String(user?.id ?? user?.user_id ?? '')
    );
    const same =
      newIds.length === oldIds.length &&
      newIds.every((id, index) => id === oldIds[index]);
    if (!same) {
      merged.related_users = [];
    }
  }

  return merged;
}

/**
 * Insert or replace a budget row in a list (new rows prepend).
 *
 * @param {Array} rows
 * @param {Object|null|undefined} saved
 * @returns {Array}
 */
export function upsertBudgetListRow(rows, saved) {
  const list = Array.isArray(rows) ? rows : [];
  if (!saved || saved.id == null) return list;
  const idx = list.findIndex(row => String(row.id) === String(saved.id));
  if (idx === -1) {
    return [saved, ...list];
  }
  const next = [...list];
  next[idx] = mergeBudgetListRow(list[idx], saved);
  return next;
}
